/**
 * ExplosionEffect - Creates explosion visual effects
 */

import * as THREE from 'three';

export class ExplosionEffect {
  private scene: THREE.Scene;
  private activeExplosions: Array<{
    particles: THREE.Group;
    startTime: number;
    duration: number;
  }>;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.activeExplosions = [];
  }

  createExplosion(position: { x: number; y: number; z: number }, size: number = 1): void {
    const explosion = new THREE.Group();
    const particleCount = 20;

    // Create explosion particles
    for (let i = 0; i < particleCount; i++) {
      const particleSize = Math.random() * 2 * size + 0.5;
      const geometry = new THREE.SphereGeometry(particleSize, 6, 6);

      // Color gradient from orange to black
      const t = i / particleCount;
      const color = new THREE.Color();
      color.setHex(t < 0.3 ? 0xff8c00 : t < 0.6 ? 0xff4400 : 0x222222);

      const material = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.8,
      });

      const particle = new THREE.Mesh(geometry, material);

      // Random direction
      const angle = Math.random() * Math.PI * 2;
      const elevation = (Math.random() - 0.5) * Math.PI;
      const speed = Math.random() * 10 + 5;

      particle.position.set(
        position.x + Math.cos(angle) * Math.cos(elevation) * 0.5,
        position.y + Math.sin(elevation) * 0.5,
        position.z + Math.sin(angle) * Math.cos(elevation) * 0.5
      );

      // Store velocity in userData
      particle.userData.velocity = {
        x: Math.cos(angle) * Math.cos(elevation) * speed,
        y: Math.sin(elevation) * speed + 5, // Initial upward velocity
        z: Math.sin(angle) * Math.cos(elevation) * speed,
      };

      explosion.add(particle);
    }

    // Add central flash
    const flashGeometry = new THREE.SphereGeometry(size * 3, 16, 16);
    const flashMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 1.0,
    });
    const flash = new THREE.Mesh(flashGeometry, flashMaterial);
    flash.position.set(position.x, position.y, position.z);
    explosion.add(flash);

    this.scene.add(explosion);

    this.activeExplosions.push({
      particles: explosion,
      startTime: Date.now(),
      duration: 2000, // 2 seconds
    });
  }

  update(deltaTime: number): void {
    const currentTime = Date.now();

    this.activeExplosions = this.activeExplosions.filter((explosion) => {
      const elapsed = currentTime - explosion.startTime;
      const progress = elapsed / explosion.duration;

      if (progress >= 1) {
        this.scene.remove(explosion.particles);
        return false;
      }

      // Update each particle
      explosion.particles.children.forEach((child) => {
        if (child instanceof THREE.Mesh) {
          const velocity = child.userData.velocity;
          
          if (velocity) {
            // Apply gravity
            velocity.y -= 9.81 * deltaTime;

            // Update position
            child.position.x += velocity.x * deltaTime;
            child.position.y += velocity.y * deltaTime;
            child.position.z += velocity.z * deltaTime;

            // Slow down (air resistance)
            velocity.x *= 0.98;
            velocity.z *= 0.98;

            // Fade out
            if (child.material instanceof THREE.MeshBasicMaterial) {
              child.material.opacity = 1 - progress;
            }

            // Expand particles
            child.scale.setScalar(1 + progress * 2);
          } else {
            // Flash effect
            if (child.material instanceof THREE.MeshBasicMaterial) {
              child.material.opacity = Math.max(0, 1 - progress * 3);
              child.scale.setScalar(1 + progress * 0.5);
            }
          }
        }
      });

      return true;
    });
  }

  dispose(): void {
    this.activeExplosions.forEach((explosion) => {
      this.scene.remove(explosion.particles);
    });
    this.activeExplosions = [];
  }
}
